"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import type { ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { useRouter } from "@/lib/navigation";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { DUPLICATE_INDEX_WORKER_NAME } from "./constants";

const DuplicateIndexWorkerConfirmQuery = graphql(`
  query DuplicateIndexWorkerConfirmQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on IndexWorker {
        id
        name
      }
    }
  }
`);

const DuplicateIndexWorkerTargetProjectQuery = graphql(`
  query DuplicateIndexWorkerTargetProjectQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on IndexWorkerProject {
        id
        name
      }
    }
  }
`);

type DuplicateIndexWorkerInput = {
  indexWorkerId: string;
  targetProjectId?: string;
};

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function DuplicateIndexWorkerConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const router = useRouter();
  const input = (part.input ?? {}) as Partial<DuplicateIndexWorkerInput>;
  const indexWorkerId = input.indexWorkerId ?? "";
  const targetProjectId = input.targetProjectId;

  const [{ data, fetching }] = useQuery({
    query: DuplicateIndexWorkerConfirmQuery,
    variables: { id: indexWorkerId },
    pause: !indexWorkerId,
  });
  const [{ data: targetProjectData, fetching: targetProjectFetching }] =
    useQuery({
      query: DuplicateIndexWorkerTargetProjectQuery,
      variables: { id: targetProjectId ?? "" },
      pause: !targetProjectId,
    });

  const indexWorkerName =
    data?.node?.__typename === "IndexWorker" ? data.node.name : "Unknown";
  const targetProjectName =
    targetProjectData?.node?.__typename === "IndexWorkerProject"
      ? targetProjectData.node.name
      : "Unknown";
  let destinationProjectContent: ReactNode = (
    <Trans>Duplicate to the same project</Trans>
  );

  if (targetProjectId) {
    destinationProjectContent = targetProjectFetching ? (
      <Skeleton className="h-4 w-24 bg-gray-400 dark:bg-gray-600" />
    ) : (
      <span className="font-medium">{targetProjectName}</span>
    );
  }

  if (
    !(isToolUIPart(part) && getToolName(part) === DUPLICATE_INDEX_WORKER_NAME)
  ) {
    return null;
  }
  if (part.state !== "input-available") return null;

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Duplicate Index Worker</Trans>
      </div>
      <ul className="mb-4 list-disc pl-5 space-y-1">
        <li>
          <Trans>Index Worker</Trans>:{" "}
          {fetching ? (
            <Skeleton className="h-4 w-20 bg-gray-400 dark:bg-gray-600" />
          ) : (
            <span className="font-medium">{indexWorkerName}</span>
          )}
        </li>
        <li>
          <Trans>Destination Project</Trans>: {destinationProjectContent}
        </li>
      </ul>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
          onSuccess={() => router.refresh()}
        >
          <Trans>Duplicate</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
